import { isEventExpiredForUi } from 'util/isEventExpiredForUi';
import React, { ReactElement } from 'react';
import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { ExpiredEventStatusIcon } from 'components/icons/ExpiredEventStatusIcon';
import type { Event } from 'services/api';
import type { SxProps, Theme } from '@mui/material/styles';

export type WaitingOnBadgeVariant = 'event-live' | 'you-approved' | 'expired' | 'denied' | 'text';

export interface WaitingOnBadgeProps {
  label: string;
  variant?: WaitingOnBadgeVariant;
}

/** Event Tracker: show Figma expired chip when status/labels indicate expired (API strings vary). */
export function isExpiredEventTrackerRow (event: Event): boolean {
  return isEventExpiredForUi(event);
}

const BADGE_MIN_WIDTH_PX = 103;

const BADGE_BOX_SX: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  minWidth: BADGE_MIN_WIDTH_PX,
  boxSizing: 'border-box',
  borderRadius: '9999px',
  padding: '4px 9px',
};

const CHIP_LABEL_SX: SxProps<Theme> = {
  fontFamily: 'Poppins',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '21px',
};

const PLAIN_TEXT_SX: SxProps<Theme> = {
  ...CHIP_LABEL_SX,
  color: '#383838',
};

type ChipVariantKey = Exclude<WaitingOnBadgeVariant, 'text'>;

type ChipDef = {
  backgroundColor: string;
  /** Sets `color` on the chip box so icon + label inherit (omit for custom label color). */
  boxColor?: string;
  label: string;
  icon: 'check' | 'expired' | 'close';
  /** Merged onto chip label Typography (e.g. expired Figma: 2937:22271). */
  labelSx?: SxProps<Theme>;
};

const CHIP_BY_VARIANT: Record<ChipVariantKey, ChipDef> = {
  'event-live': {
    backgroundColor: '#00AD0B',
    boxColor: '#FFFFFF',
    label: 'Event Live',
    icon: 'check',
  },
  'you-approved': {
    backgroundColor: '#4167FF',
    boxColor: '#FFFFFF',
    label: 'You Approved',
    icon: 'check',
  },
  expired: {
    backgroundColor: '#E8EAEF',
    label: 'Expired',
    icon: 'expired',
    labelSx: { color: '#383838', letterSpacing: 0, whiteSpace: 'nowrap' },
  },
  // Figma: Active-Tickets 2937:22132 (rejected event request row).
  denied: {
    backgroundColor: '#9B0000',
    boxColor: '#FFFFFF',
    label: 'Denied',
    icon: 'close',
  },
};

const getVariantFromLabel = (label: string): WaitingOnBadgeVariant => {
  const lowerLabel = label.trim().toLowerCase();
  if (lowerLabel === 'event live') return 'event-live';
  if (lowerLabel === 'you approved') return 'you-approved';
  if (lowerLabel === 'expired') return 'expired';
  if (lowerLabel === 'denied') return 'denied';
  return 'text';
};

export const WaitingOnBadge = ({
  label,
  variant,
}: WaitingOnBadgeProps): ReactElement => {
  const computedVariant: WaitingOnBadgeVariant = variant ?? getVariantFromLabel(label);

  if (computedVariant === 'text') {
    return (
      <Typography sx={PLAIN_TEXT_SX}>
        {label}
      </Typography>
    );
  }

  const chip = CHIP_BY_VARIANT[computedVariant];

  return (
    <Box
      sx={{
        ...BADGE_BOX_SX,
        backgroundColor: chip.backgroundColor,
        ...(chip.boxColor !== undefined && { color: chip.boxColor }),
      }}
    >
      {chip.icon === 'check' ? (
        <CheckIcon sx={{ fontSize: 14 }} />
      ) : chip.icon === 'close' ? (
        <CloseIcon sx={{ fontSize: 14 }} />
      ) : (
        <Box
          sx={{
            width: '10px',
            height: '10px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ExpiredEventStatusIcon />
        </Box>
      )}
      <Typography sx={{ ...CHIP_LABEL_SX, ...chip.labelSx }}>
        {chip.label}
      </Typography>
    </Box>
  );
};

export default WaitingOnBadge;
